import React, { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { X, Trash2, Check, ListChecks } from "lucide-react";
import ProductAddToCart from "../routs/productPage/ProductAddToCart";
import PopUp from "../containers/PopUp";
import CartShoppingListPicker from "../routs/cart/CartShoppingListPicker";
import { upsertCartItem, clearCartAll } from "../../server/CollectSite";
import { setToggleCart } from "../../server/CartsOpen";
import { setFilterSort } from "../../server/Product";
import { newGlobalFileServer } from "../../../axiosInstance";
import { getDefaultMediaPath } from "../../utils/productMedia";
import {
  getProductImageUrl,
  getResizedProductImageUrl,
} from "../../utils/productImageLoader";
import { canProceedToCheckout } from "../../utils/cartCheckout";
import {
  getMinCheckoutChargeMessage,
  isBelowMinCheckoutCharge,
} from "../../constants/checkout";
import { showAlertWithConfirm } from "../../server/ShowAlert";

import {
  isCartDrawerRoute,
  canUseCartDrawer,
} from "../../utils/cartDrawerRoutes";
import {
  getCartLineUpsertPromoId,
  expandCartItemsToPackageLines,
  totalQuantityAfterRemovingPackageLine,
} from "../../utils/packageCartLines";
import ProductInfoLine from "../productInfo/ProductInfoLine";

const EMPTY_CART_ITEMS = [];

/** True once window ≥ 1024px; re-evaluates on resize. */
function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(min-width: 1024px)").matches,
  );
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const handler = (e) => setIsDesktop(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return isDesktop;
}

function cartLineImage(product) {
  const path = getDefaultMediaPath(product?.media);
  if (!path) {
    const placeholder = `${newGlobalFileServer}placeholder.jpg`;
    return { src: placeholder, fallback: placeholder };
  }
  return {
    src: getResizedProductImageUrl(path, 150),
    fallback: getProductImageUrl(path),
  };
}

function readShellOffsetPx(prop) {
  const value = parseFloat(
    getComputedStyle(document.documentElement).getPropertyValue(prop).trim(),
  );
  return Number.isFinite(value) && value > 0 ? value : null;
}

/**
 * Desktop permanent cart anchors under L1 categories only — sub-nav rows
 * extend horizontally beside it. Mobile slide-out uses the full header stack.
 */
function measureRetailHeaderBottomPx(anchorAtParentCategories) {
  if (anchorAtParentCategories) {
    const parentBottom =
      readShellOffsetPx("--shell-parent-cats-bottom") ??
      readShellOffsetPx("--shell-header-offset");
    if (parentBottom != null) return parentBottom;

    const mainCats = document.querySelector(".navigator nav.main");
    if (mainCats) return mainCats.getBoundingClientRect().bottom;
  }

  const fixedBottom = readShellOffsetPx("--shell-fixed-stack-bottom");
  if (fixedBottom != null) return fixedBottom;

  const wrap = document.querySelector(".header-wrapper");
  const row = document.querySelector(".header-second-row");
  const bottoms = [];
  if (wrap) bottoms.push(wrap.getBoundingClientRect().bottom);
  if (row) bottoms.push(row.getBoundingClientRect().bottom);
  if (!bottoms.length) {
    const header = document.querySelector("header");
    if (header) bottoms.push(header.getBoundingClientRect().bottom);
  }
  return bottoms.length ? Math.max(...bottoms) : 0;
}

const CartDrawer = ({ setProductPopUp, setSelectedProd }) => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const isDesktop = useIsDesktop();
  const [clearConfirm, setClearConfirm] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [showListPicker, setShowListPicker] = useState(false);
  const clearConfirmTimer = useRef(null);

  const openCart = useSelector((state) => state.openCart.openCart);
  const role = useSelector((state) => state.user?.role);
  const user = useSelector((state) => state.user?.user);
  const isClientView = useSelector((state) => state.category?.isClientView);
  const authDialogOpen = useSelector((state) =>
    Boolean(state.user?.userEntry?.[0]),
  );
  const canShowCart =
    canUseCartDrawer(user, role, isClientView) && !authDialogOpen;
  const cartOpen = Boolean(openCart && canShowCart);
  const isCategoryRoute = isCartDrawerRoute(pathname);
  /* Cart is visible on home + category/search/sales after login. On desktop it opens
     automatically; on mobile it follows the toggle. Hidden for guests and auth dialog. */
  const drawerOpen = canShowCart && isCategoryRoute && (isDesktop || cartOpen);

  const productsInCart = useSelector(
    (state) => state.productsInCart?.productsInCart ?? EMPTY_CART_ITEMS,
  );
  const orderSum = useSelector((state) => state.productsInCart?.orderSum);
  const stationId = useSelector((state) => state.user?.stationId);
  const minCheckoutChargeAmount = useSelector(
    (state) => state.productsInCart?.minCheckoutChargeAmount,
  );
  const couponDiscount = useSelector(
    (state) => state.productsInCart?.couponDiscount,
  );

  const subtotal = useMemo(
    () => productsInCart.reduce((t, item) => t + item.totalPrice, 0),
    [productsInCart],
  );

  const saleTotal = useMemo(
    () =>
      productsInCart.reduce((total, item) => {
        if (item.promoId) {
          return total + (item.unitPrice * item.quantity - item.totalPrice);
        }
        return total;
      }, 0),
    [productsInCart],
  );

  const totalPay = parseFloat(orderSum ?? subtotal);
  const chargeAmount = Math.max(0, totalPay - Number(couponDiscount || 0));

  const itemQty = useMemo(
    () => productsInCart.reduce((n, p) => n + (Number(p.quantity) || 0), 0),
    [productsInCart],
  );
  const displayLines = useMemo(
    () => expandCartItemsToPackageLines(productsInCart),
    [productsInCart],
  );

  const closeDrawer = () => {
    dispatch(setToggleCart(false));
    dispatch(setFilterSort(false));
  };

  const handleProceedToCheckout = (event) => {
    if (isBelowMinCheckoutCharge(chargeAmount, minCheckoutChargeAmount)) {
      event.preventDefault();
      showAlertWithConfirm({
        msgTitle: "לא ניתן להמשיך לתשלום",
        msgText: getMinCheckoutChargeMessage(minCheckoutChargeAmount),
        confirmText: "הבנתי",
      });
      return;
    }
    if (!canProceedToCheckout(productsInCart)) {
      event.preventDefault();
      return;
    }
    closeDrawer();
  };

  /* body class only needed on mobile (desktop cart is always open) */
  useEffect(() => {
    if (!isDesktop && cartOpen) {
      document.body.classList.add("cart-drawer-open");
    } else {
      document.body.classList.remove("cart-drawer-open");
    }
    return () => document.body.classList.remove("cart-drawer-open");
  }, [isDesktop, cartOpen]);

  /* Lets page layouts center content in the area beside the permanent desktop cart. */
  useEffect(() => {
    const permanentDesktop = isDesktop && drawerOpen;
    if (permanentDesktop) {
      document.body.classList.add("cart-drawer-desktop-visible");
    } else {
      document.body.classList.remove("cart-drawer-desktop-visible");
    }
    return () => document.body.classList.remove("cart-drawer-desktop-visible");
  }, [isDesktop, drawerOpen]);

  const anchorCartAtParentCategories = isDesktop && drawerOpen;

  /** Keep drawer below the header; desktop permanent cart ignores sub-nav height. */
  useEffect(() => {
    if (typeof ResizeObserver === "undefined") {
      return undefined;
    }

    const root = document.documentElement;
    const wrap = document.querySelector(".header-wrapper");
    const mainCats = document.querySelector(".navigator nav.main");
    const row = document.querySelector(".header-second-row");
    const observed = [wrap, mainCats, row].filter(Boolean);

    if (!observed.length) {
      return undefined;
    }

    const syncTop = () => {
      const bottom = Math.ceil(
        measureRetailHeaderBottomPx(anchorCartAtParentCategories),
      );
      root.style.setProperty(
        "--cart-drawer-top-offset",
        `${Math.max(0, bottom + 2)}px`,
      );
    };

    syncTop();
    const ro = new ResizeObserver(() => {
      requestAnimationFrame(syncTop);
    });
    observed.forEach((el) => ro.observe(el));
    window.addEventListener("resize", syncTop);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", syncTop);
      root.style.removeProperty("--cart-drawer-top-offset");
    };
  }, [anchorCartAtParentCategories]);

  useEffect(() => {
    if (!drawerOpen) return undefined;
    const id = requestAnimationFrame(() => {
      const bottom = Math.ceil(
        measureRetailHeaderBottomPx(anchorCartAtParentCategories),
      );
      document.documentElement.style.setProperty(
        "--cart-drawer-top-offset",
        `${Math.max(0, bottom + 2)}px`,
      );
    });
    return () => cancelAnimationFrame(id);
  }, [drawerOpen, anchorCartAtParentCategories]);

  useEffect(() => {
    if (!canShowCart) {
      dispatch(setToggleCart(false));
      dispatch(setFilterSort(false));
    }
  }, [canShowCart, dispatch]);

  useEffect(() => {
    // On mobile only: close cart when navigating away from filter routes.
    if (!isDesktop && !isCartDrawerRoute(pathname)) {
      dispatch(setToggleCart(false));
      dispatch(setFilterSort(false));
    }
  }, [pathname, dispatch, isDesktop]);

  const handleDelete = (line) => {
    const source = line.item;
    dispatch(
      upsertCartItem(
        source.productId,
        parseFloat(source.unitPrice).toFixed(2),
        totalQuantityAfterRemovingPackageLine(source, line.lineKind),
        String(stationId),
        getCartLineUpsertPromoId(source.product, source.promoId),
        source.product,
      ),
    );
  };

  const handleClearCart = () => {
    if (!clearConfirm) {
      setClearConfirm(true);
      clearConfirmTimer.current = setTimeout(
        () => setClearConfirm(false),
        3000,
      );
    } else {
      clearTimeout(clearConfirmTimer.current);
      setClearConfirm(false);
      dispatch(clearCartAll(stationId));
    }
  };

  return (
    <>
      <div
        className={`cart-drawer-root${drawerOpen ? " cart-drawer-root--open" : ""}`}
        aria-hidden={!isDesktop && !cartOpen}
      >
        <aside
          className={`cart-drawer${isDesktop && drawerOpen ? " cart-drawer--desktop-permanent" : ""}${cartOpen ? " cart-drawer--open" : ""}`}
          aria-label="עגלת הקניות"
          dir="rtl"
        >
          <header className="cart-drawer__head">
            <div className="cart-drawer__head-text">
              <h2 className="cart-drawer__head-title">עגלת הקניות</h2>
              {itemQty > 0 ? (
                <p className="cart-drawer__head-sub">{itemQty} פריטים</p>
              ) : null}
            </div>
            {productsInCart.length > 0 && (
              <button
                type="button"
                className={`cart-drawer__clear-btn${clearConfirm ? " cart-drawer__clear-btn--confirm" : ""}`}
                onClick={handleClearCart}
                aria-label={
                  clearConfirm ? "לחץ לאישור ריקון העגלה" : "רוקן עגלה"
                }
              >
                {clearConfirm ? (
                  <>
                    <Check size={13} strokeWidth={2.5} aria-hidden />
                    <span>אישור?</span>
                  </>
                ) : (
                  <Trash2 size={15} strokeWidth={2} aria-hidden />
                )}
              </button>
            )}
            {/* Close button only on mobile */}
            {!isDesktop && (
              <button
                type="button"
                className="cart-drawer__head-close"
                onClick={closeDrawer}
                aria-label="סגור"
              >
                <X size={20} strokeWidth={2} aria-hidden />
              </button>
            )}
          </header>

          <>
            <div className="cart-drawer__body">
              <div className="cart-drawer__list">
                {productsInCart.length === 0 ? (
                  <div className="co-empty-cart">
                    <p className="co-empty-cart__message">העגלה ריקה 🛒</p>
                    <button
                      type="button"
                      className="co-empty-cart__btn"
                      onClick={() => setShowListPicker(true)}
                    >
                      <ListChecks size={18} strokeWidth={2} aria-hidden />
                      בחר מרשימת קניות
                    </button>
                  </div>
                ) : (
                  displayLines.map((line) => {
                    const element = line.item;
                    const img = cartLineImage(element.product);
                    const openProduct = () => {
                      setProductPopUp(true);
                      setSelectedProd(element.product);
                    };
                    return (
                      <article
                        key={line.lineKey}
                        className={`cart-drawer-line cart-drawer-line--narrow${
                          line.isBundleLine ? " cart-drawer-line--bundle" : ""
                        }`}
                      >
                        <div className="cart-drawer-line__layout">
                          <button
                            type="button"
                            className="cart-drawer-line__thumb-btn"
                            onClick={openProduct}
                            aria-label={`פרטי מוצר: ${element.product?.name ?? ""}`}
                          >
                            <span className="cart-drawer-line__thumb-wrap">
                              <img
                                loading="lazy"
                                decoding="async"
                                src={img.src}
                                alt=""
                                className="cart-drawer-line__thumb-img"
                                onError={(e) => {
                                  // One-shot fallback. Never compare against
                                  // e.currentTarget.src — the browser returns
                                  // it percent-encoded, so Hebrew filenames
                                  // would never match and retry forever.
                                  if (e.currentTarget.dataset.fellBack) return;
                                  e.currentTarget.dataset.fellBack = "true";
                                  e.currentTarget.src = img.fallback;
                                }}
                              />
                            </span>
                          </button>
                          <div className="cart-drawer-line__content">
                            <div className="cart-drawer-line__top">
                              <button
                                type="button"
                                className="cart-drawer-line__title-block"
                                onClick={openProduct}
                              >
                                <span className="cart-drawer-line__name">
                                  {element.product?.name}
                                </span>
                              </button>
                              {pendingDeleteId === line.lineKey ? (
                                <span className="co-delete-confirm co-delete-confirm--inline">
                                  <button
                                    type="button"
                                    className="co-delete-confirm__yes"
                                    onClick={() => {
                                      handleDelete(line);
                                      setPendingDeleteId(null);
                                    }}
                                  >
                                    הסר
                                  </button>
                                  <button
                                    type="button"
                                    className="co-delete-confirm__no"
                                    onClick={() => setPendingDeleteId(null)}
                                  >
                                    ביטול
                                  </button>
                                </span>
                              ) : (
                                <button
                                  type="button"
                                  className="cart-drawer-line__dismiss-header"
                                  aria-label="הסר מהעגלה"
                                  onClick={() =>
                                    setPendingDeleteId(line.lineKey)
                                  }
                                >
                                  <X size={14} strokeWidth={2} aria-hidden />
                                </button>
                              )}
                            </div>
                            {line.dealNote ? (
                              <div className="cart-drawer-line__deal-note">
                                {line.dealNote}
                              </div>
                            ) : null}
                            <div className="cart-drawer-line__bottom">
                              <button
                                type="button"
                                className="cart-drawer-line__details-hit"
                                onClick={openProduct}
                              >
                                <div className="cart-drawer-line__details-zone">
                                  <div className="cart-drawer-line__details-lines">
                                    <ProductInfoLine
                                      product={element.product}
                                    />
                                    {element.product?.description ? (
                                      <span className="cart-drawer-line__desc">
                                        {element.product.description}
                                      </span>
                                    ) : null}
                                  </div>
                                </div>
                              </button>
                              <div
                                className="cart-drawer-line__qty-zone"
                                onClick={(e) => e.stopPropagation()}
                                onKeyDown={(e) => e.stopPropagation()}
                              >
                                <div className="cart-drawer-line__qty">
                                  <ProductAddToCart
                                    element={element}
                                    haveInfo={false}
                                    retailCard
                                    lineKind={line.lineKind}
                                    onRequestRemove={() =>
                                      setPendingDeleteId(line.lineKey)
                                    }
                                  />
                                </div>
                              </div>
                              <button
                                type="button"
                                className="cart-drawer-line__price-hit"
                                onClick={openProduct}
                              >
                                <span className="cart-drawer-line__price-block">
                                  <span className="cart-drawer-line__price-num">
                                    {Number(line.lineTotal).toFixed(2)}
                                  </span>
                                  <span className="cart-drawer-line__price-sym">
                                    {" "}
                                    ₪
                                  </span>
                                </span>
                              </button>
                            </div>
                          </div>
                        </div>
                      </article>
                    );
                  })
                )}
              </div>

              <div className="cart-drawer__summary">
                <NavLink
                  to="/cart"
                  className={`cart-drawer__cta cart-drawer__summary-cta${itemQty === 0 ? " cart-drawer__cta--disabled" : ""}`}
                  onClick={handleProceedToCheckout}
                  aria-disabled={itemQty === 0}
                >
                  <span className="cart-drawer__cta-pay">
                    <span className="cart-drawer__cta-pay-total">
                      {totalPay.toFixed(2)} ₪
                    </span>
                    {saleTotal > 0 ? (
                      <span className="cart-drawer__cta-pay-saved">
                        חסכת {saleTotal.toFixed(2)} ₪
                      </span>
                    ) : null}
                  </span>
                  <span className="cart-drawer__cta-label">המשך לתשלום</span>
                </NavLink>
              </div>
            </div>
          </>
        </aside>
      </div>

      {showListPicker ? (
        <PopUp
          width="480px"
          close={() => setShowListPicker(false)}
          wrapperClassName="popup-wrapper--shopping-list"
        >
          <CartShoppingListPicker close={() => setShowListPicker(false)} />
        </PopUp>
      ) : null}
    </>
  );
};

export default CartDrawer;
